import React, { useEffect, useRef, useState } from "react";
import {
  AddFlex,
  CustomText,
  PrimaryActionButton,
  SecondaryActionButton,
} from "../reusableStyles";
import { callNonStoreApi } from "../NonStoreApis";
import {
  DuplicateCampaign,
  FetchAllCampaigns,
  FetchCollabCampaigns,
  FetchManagedCampaigns,
  ToggleCampaignStatus,
  UpdateCollaborationRequestStatus,
} from "../Api/ApiEndPoints";
import { useSelector } from "react-redux";
import {
  Lock,
  Unlock,
  Bot,
  Calendar,
  User,
  MoreVertical,
  Clock,
  ChevronRight,
  Video,
  Image,
  Camera,
  Loader,
} from "lucide-react";
import { useNavigate } from "react-router";
import {
  INSTAGRAM_COLLABORATIONS_DETAILS,
  INSTAGRAM_UPDATE_MANAGED_CAMPAIGN,
  UPDATE_INSTAGRAM_CAMPAIGNS,
} from "../OnboardingPage/routeNames";
import { capitalizeFirstLetter, isStringNullOrUndefined } from "../utils";
import useInstagramIntegrationHook from "../CustomHooks/useInstagramIntegrationHook";
import { useLayout } from "../CustomHooks.js/LayoutContext";
import InstagramAccountSwitcher from "../Integrations/Instagram/InstagramAccountSwitcher";
import useDialogActionsHook from "../CustomHooks.js/useDialogActionsHook";
import Tabs from "../Tabs";
import { enqueueSnackbar } from "notistack";
import { CircularProgress } from "@mui/material";
import { primaryActionColor, primaryActionColorLight } from "../constants";

function Collaborations() {
  const { setLayoutComponent, clearLayoutComponent } = useLayout();
  const navigate = useNavigate();
  const [collaborationsTabIndex, setCollaborationsTabIndex] = useState(0);
  const [collaborations, setCollaborations] = useState({
    invited: [],
    accepted: [],
    rejected: [],
  });
  const [campaigns, setCampaigns] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const { credentials } = useSelector((state) => state.instagram);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [collaborationStatusUpdating, setCollaborationStatusUpdating] =
    useState(false);

  const formatCollaborations = (collaborations) => {
    let collabs = {};
    collaborations.forEach((data) => {
      collabs[data.status] = data.campagins;
    });
    setCollaborations(collabs);
  };

  const fetchCampaigns = async () => {
    try {
      setIsLoading(true);
      const response = await callNonStoreApi(FetchCollabCampaigns, {
        username: credentials?.username,
      });
      formatCollaborations(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    setCampaigns([]);
    fetchCampaigns();
  }, [credentials]);

  useEffect(() => {
    setLayoutComponent(InstagramAccountSwitcher);
    return () => {
      clearLayoutComponent();
    };
  }, []);

  const gatCollaborationStatus = (campagin) => {
    return campagin.collabStatus;
  };

  const getStatusColor = (status) => {
    const statusColors = {
      live: "bg-emerald-100 text-emerald-800 border-emerald-200",
      draft: "bg-gray-100 text-gray-800 border-gray-200",
      paused: "bg-amber-100 text-amber-800 border-amber-200",
      archived: "bg-red-100 text-red-800 border-red-200",
    };
    return statusColors[status] || statusColors.draft;
  };

  const getBorderColor = (status) => {
    const borderColors = {
      live: "border-l-emerald-500",
      draft: "border-l-gray-500",
      paused: "border-l-amber-500",
      archived: "border-l-red-500",
    };
    return borderColors[status] || borderColors.draft;
  };

  const handleCardClick = (e, campaign) => {
    // If clicking the menu or menu button, let those handlers work
    if (
      e.target.closest(".menu-button") ||
      e.target.closest(".dropdown-menu")
    ) {
      return;
    }
    if (openMenuId) {
      return setOpenMenuId(null);
    }
    // Otherwise navigate and close menu
    navigate(
      INSTAGRAM_COLLABORATIONS_DETAILS.replace(
        ":campaignId",
        campaign._id
      ).replace(":instaUsername", credentials.username)
    );
    setOpenMenuId(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffInSeconds = Math.floor((now - past) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const getAutomationCounts = (automations) => {
    return {
      reels: automations.filter((a) => !a.mediaData.isStory).length,
      stories: automations.filter((a) => a.mediaData.isStory).length,
    };
  };
  const menuItems = (campagin) => {
    if (campagin.collaboration?.status === "invited") {
      return [
        {
          id: "accepted",
          name: "Accept invitation",
          action: () => {
            // Api call to accept invitation
          },
        },
        {
          id: "rejected",
          name: "Reject invitation",
          action: () => {
            // Api call to reject invitation
          },
        },
      ];
    }
    return [];
  };

  const handleCollaborationRequestUpdate = async (status, campaginId) => {
    try {
      setCollaborationStatusUpdating(true);
      const response = await callNonStoreApi(UpdateCollaborationRequestStatus, {
        status,
        username: credentials.username,
        campaginId,
      });
      formatCollaborations(response.data.collaborations);
      enqueueSnackbar(`Collaboration ${capitalizeFirstLetter(status)}`);
    } catch (error) {
      enqueueSnackbar(`Something went wrong`, {
        variant: "error",
      });
      console.log(error);
    } finally {
      setCollaborationStatusUpdating(false);
    }
  };

  useEffect(() => {
    const status =
      collaborationsTabIndex === 0
        ? "accepted"
        : collaborationsTabIndex === 1
        ? "invited"
        : "rejected";
    setCampaigns(collaborations[status]);
  }, [collaborationsTabIndex, collaborations, credentials]);

  return isLoading ? (
    <Loader />
  ) : (
    <div className="container mx-auto px-0 py-0">
      <Tabs
        tabs={[
          {
            name: (
              <CollaborationTabName
                name={"Accepted"}
                requestsNumber={collaborations.accepted?.length || 0}
              />
            ),
          },
          {
            name: (
              <CollaborationTabName
                name={"Invited"}
                requestsNumber={collaborations.invited?.length || 0}
              />
            ),
          },
          {
            name: (
              <CollaborationTabName
                name={"Rejected"}
                requestsNumber={collaborations.rejected?.length || 0}
              />
            ),
          },
        ]}
        selectedTabIndex={collaborationsTabIndex}
        setSelectedTabIndex={setCollaborationsTabIndex}
      />
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-6">
        {campaigns?.map((campaign) => {
          const automationCounts = getAutomationCounts(campaign.automations);

          return (
            <div
              key={campaign._id}
              className={`bg-white rounded-lg border shadow-sm hover:shadow-lg transition-all duration-200 border-l-4 ${getBorderColor(
                campaign.status
              )} group cursor-pointer relative`}
              onClick={(e) => handleCardClick(e, campaign)}
            >
              {campaign.collabStatus === "invited" && (
                <AddFlex
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  style={{
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    left: 0,
                    top: 0,
                    borderRadius: "8px",
                    backgroundColor: "#000000B3",
                  }}
                  flexDirection="column"
                  gap="10px"
                  alignItems="center"
                  justify="center"
                >
                  <CustomText color="white" fontWeight="500">
                    New Collaboration from {"Brand/Agency"}
                  </CustomText>
                  {collaborationStatusUpdating ? (
                    <CircularProgress size={16} sx={{ color: "white" }} />
                  ) : (
                    <AddFlex alignItems="center" justify="center" gap="10px">
                      <SecondaryActionButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCollaborationRequestUpdate(
                            "rejected",
                            campaign._id
                          );
                        }}
                      >
                        Reject
                      </SecondaryActionButton>
                      <PrimaryActionButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCollaborationRequestUpdate(
                            "accepted",
                            campaign._id
                          );
                        }}
                      >
                        Accept
                      </PrimaryActionButton>
                    </AddFlex>
                  )}
                </AddFlex>
              )}
              <div className="p-6">
                <div className="flex flex-col h-full">
                  {/* Header Section */}
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h2 className="text-xl font-semibold group-hover:text-blue-600 transition-colors">
                          {campaign.name}
                        </h2>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                            campaign.status
                          )}`}
                        >
                          {capitalizeFirstLetter(campaign.status)}
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                            campaign.status
                          )}`}
                        >
                          {capitalizeFirstLetter(
                            gatCollaborationStatus(campaign)
                          )}
                        </span>
                        <ChevronRight className="h-5 w-5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Clock className="h-4 w-4" />
                        <span>Created {getTimeAgo(campaign.createdAt)}</span>
                      </div>
                    </div>
                    {campaign.collaboration?.status === "invited" && (
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpenMenuId(
                              openMenuId === campaign._id ? null : campaign._id
                            );
                          }}
                          className="hover:bg-gray-100 p-2 rounded-full"
                        >
                          <MoreVertical className="h-5 w-5 text-gray-500" />
                        </button>
                        {openMenuId === campaign._id && (
                          <div
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border py-1 z-10"
                          >
                            {menuItems(campaign).map((item) => (
                              <button
                                className="w-full px-4 py-2 text-left hover:bg-gray-50"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  item.action();
                                  setOpenMenuId(null);
                                }}
                              >
                                {item.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Automation Types */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-6">
                    <p className="text-sm font-medium text-gray-700 mb-3">
                      Automation Types
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="flex items-center gap-2">
                        <Video className="h-4 w-4 text-gray-600" />
                        <div>
                          <p className="text-xs text-gray-500">Posts / Reels</p>
                          <p className="font-semibold">
                            {automationCounts.reels}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Camera className="h-4 w-4 text-gray-600" />
                        <div>
                          <p className="text-xs text-gray-500">Stories</p>
                          <p className="font-semibold">
                            {automationCounts.stories}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer Section */}
                  <div className="mt-auto pt-4 border-t border-gray-100">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <div className="bg-gray-100 p-1.5 rounded-full">
                          <User className="h-4 w-4 text-gray-600" />
                        </div>
                        <span className="text-sm text-gray-600">
                          {`Created by ${campaign.createdBy.email}`}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(campaign.updatedAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Collaborations;

const CollaborationTabName = ({ name, requestsNumber }) => {
  return (
    <AddFlex alignItems="center" gap="10px">
      {name}
      <AddFlex
        alignItems="center"
        justify="center"
        style={{
          width: "20px",
          height: "20px",
          borderRadius: "100%",
          backgroundColor: primaryActionColorLight,
        }}
      >
        <CustomText color={primaryActionColor} fontSize={"12px"}>
          {requestsNumber}
        </CustomText>
      </AddFlex>
    </AddFlex>
  );
};
